// GlobalCheckboxes.tsx
import React from 'react';
import { Checkbox } from 'primereact/checkbox';

export interface CheckboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface GlobalCheckboxesProps {
  label?: string;
  options: CheckboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  layout?: 'horizontal' | 'vertical';
  gap?: number;
}

export const GlobalCheckboxes: React.FC<GlobalCheckboxesProps> = ({
  label,
  options,
  value,
  onChange,
  required = false,
  disabled = false,
  error = '',
  layout = 'horizontal',
  gap = 24,
}) => {
  const handleCheckboxChange = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#ef4444' : '#1e3a8a',
          marginBottom: '12px'
        }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}
      
      <div style={{
        display: 'flex',
        flexDirection: layout === 'horizontal' ? 'row' : 'column',
        gap: `${gap}px`,
        flexWrap: 'wrap'
      }}>
        {options.map((option) => (
          <div 
            key={option.value} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px' 
            }}
          >
            <Checkbox
              inputId={`checkbox-${option.value}`}
              value={option.value}
              onChange={() => handleCheckboxChange(option.value)}
              checked={value.includes(option.value)}
              disabled={disabled || option.disabled}
              className="global-checkbox"
            />
            <label
              htmlFor={`checkbox-${option.value}`}
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: value.includes(option.value) ? '#1e3a8a' : '#64748b',
                cursor: (disabled || option.disabled) ? 'not-allowed' : 'pointer',
                userSelect: 'none',
                transition: 'color 0.2s ease'
              }}
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>
      
      {error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '12px',
          color: '#ef4444',
          fontWeight: 500
        }}>
          {error}
        </span>
      )}

      <style>{`
        .global-checkbox .p-checkbox-box {
          width: 20px;
          height: 20px;
          border: 2px solid #94a3b8;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .global-checkbox .p-checkbox-box:hover {
          border-color: #3b82f6;
        }

        .global-checkbox .p-checkbox-box.p-highlight {
          border-color: #3b82f6;
          background: #3b82f6;
        }

        .global-checkbox .p-checkbox-box .p-checkbox-icon {
          color: white;
          font-size: 12px;
        }

        .global-checkbox .p-checkbox-box.p-disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};